import React, { useEffect, useRef } from "react";

// Keep your lerpColor function
function lerpColor(a, b, amount) { 
  const ah = parseInt(a.replace(/#/g, ''), 16);
  const ar = ah >> 16, ag = ah >> 8 & 0xff, ab = ah & 0xff;
  const bh = parseInt(b.replace(/#/g, ''), 16);
  const br = bh >> 16, bg = bh >> 8 & 0xff, bb = bh & 0xff;
  const rr = Math.round(ar + amount * (br - ar));
  const rg = Math.round(ag + amount * (bg - ag));
  const rb = Math.round(ab + amount * (bb - ab));
  return '#' + (((1 << 24) + (rr << 16) + (rg << 8) + (rb)).toString(16).slice(1));
}

const DialChart = ({ value = 0, size = 200, arrow = "down", title = "" , maxValue = 300}) => {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    container.innerHTML = ""; // Clear previous SVG if any

    const strokeWidth = size / 8;

    // Create SVG
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", size);
    svg.setAttribute("height", size);

    // Arc
    const slice = document.createElementNS("http://www.w3.org/2000/svg", "path");
    slice.setAttribute("fill", "none");
    slice.setAttribute("stroke-width", strokeWidth);
    slice.setAttribute("transform", `translate(${strokeWidth/2},${strokeWidth/2})`);
    svg.appendChild(slice);

    // Text in center
    const fontSize = size / 3.5;
    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("x", size/2);
    text.setAttribute("y", size/2 + fontSize/4);
    text.setAttribute("font-size", fontSize);
    text.setAttribute("text-anchor", "middle");
    svg.appendChild(text);

    // Arrow
    const arrowSize = size / 10;
    const m = arrow === "up" ? -1 : 1;
    const arrowYOffset = arrow === "up" ? arrowSize/2 : 0;
    const arrowPosX = (size/2 - arrowSize/2);
    const arrowPosY = (size - size/3) + arrowYOffset;
    const arrowDOffset = m * (arrowSize / 1.5);
    const arrowEl = document.createElementNS("http://www.w3.org/2000/svg", "path");
    arrowEl.setAttribute("d", `M 0 0 ${arrowSize} 0 ${arrowSize/2} ${arrowDOffset} 0 0 Z`);
    arrowEl.setAttribute("opacity", "0.6");
    arrowEl.setAttribute("transform", `translate(${arrowPosX},${arrowPosY})`);
    svg.appendChild(arrowEl);

    // Append SVG to container
    container.appendChild(svg);

    // Arc helpers
    const polarToCartesian = (cx, cy, r, angleDeg) => {
      const rad = (angleDeg-90) * Math.PI / 180;
      return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
    };
    const describeArc = (x, y, r, startAngle, endAngle) => {
      const start = polarToCartesian(x, y, r, endAngle);
      const end = polarToCartesian(x, y, r, startAngle);
      const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
      return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
    };

    // Set value function
    const setValue = (v) => {
        const cappedValue = Math.min(v, maxValue);

        // Dial angle: always 360° at 100
        let c = (cappedValue / 100) * 360;
        if (c >= 360) c = 359.99; // cap at 360°
        const xy = size/2 - strokeWidth/2;
        slice.setAttribute("d", describeArc(xy, xy, xy, 270, 270 + c));

        // Color mapping
        let color;
        if (cappedValue <= 100) color = lerpColor("#EE4B2B", "#90ee90", cappedValue / 100);
        else color = lerpColor("#90ee90", "#EE4B2B", (cappedValue - 100) / (maxValue - 100));

        slice.setAttribute("stroke", color);
        arrowEl.setAttribute("fill", color);
        text.setAttribute("fill", color);

        const tspanSize = fontSize / 3;
        text.innerHTML = Math.floor(cappedValue) + `<tspan font-size="${tspanSize}" dy="${-tspanSize*1.2}">%</tspan>`;
        };

    // Animate
    let v = 0;
    const interval = setInterval(() => {
      const p = +(v/value).toFixed(2);
      const a = (p < 0.95) ? 2 - (2*p) : 0.05;
      v += a;
      if (v >= value) { v = value; clearInterval(interval); }
      setValue(v);
    }, 10);

    return () => clearInterval(interval);

  }, [value, size, arrow]);

    return (
    <div
      className="chart"
      style={{
        display: "inline-block",
        textAlign: "center",
        backgroundColor: "transparent",
        padding: "6px 10px",
        borderRadius: "8px",
        border: "none",
        boxShadow: "none",
        color: "#e7edf7",
      }}
    >
        {title && <div className="chart-title" style={{ color: "#e7edf7", marginBottom: "6px" }}>{title}</div>}
        <div ref={containerRef}></div>
    </div>
    );
};

export default DialChart;
